import { useState, type Ref } from 'react';
import { Icon } from './Icon.js';

type NumberProps = { label: string; value: number; onChange: (value: number) => void; min?: number; max?: number; step?: number; suffix?: string };
type NumericProps = Omit<NumberProps, 'label' | 'suffix'> & { label?: string; className?: string; mode?: 'decimal' | 'numeric'; ref?: Ref<HTMLInputElement> };

/**
 * Campo numérico que aceita vazio: 0 aparece como campo em branco e apagar tudo devolve 0.
 * O rascunho só vale enquanto representa o número do pai, senão um reset externo (Limpar)
 * continuaria mostrando o que já tinha sido digitado.
 */
export function NumericInput({ value, onChange, label, className = 'field numeric', min = 0, max, step = .01, mode = 'decimal', ref }: NumericProps) {
  const [draft, setDraft] = useState('');
  const text = (Number(draft) || 0) === value ? draft : (value === 0 ? '' : String(value));
  return (
    <input
      ref={ref} className={className} type="number" inputMode={mode} aria-label={label}
      min={min} max={max} step={step} value={text}
      onChange={(event) => { setDraft(event.target.value); onChange(Number(event.target.value) || 0); }}
    />
  );
}

export function NumberField({ label, value, onChange, min = 0, max, step = .01, suffix = 'R$' }: NumberProps) {
  return (
    <label className="field-label">
      {label}
      <span className="field-shell">
        <NumericInput className="field numeric with-suffix" value={value} onChange={onChange} min={min} max={max} step={step} />
        <span className="field-suffix">{suffix}</span>
      </span>
    </label>
  );
}

export function RangeField({ label, value, onChange, min = 0, max = 100, step = 1, marks }: NumberProps & { marks?: number[] }) {
  return (
    <label className="field-label">
      <span className="range-row"><span>{label}</span><span className="range-value">{value}</span></span>
      <input type="range" min={min} max={max} step={step} value={value} onChange={(event) => onChange(Number(event.target.value) || 0)} />
      {marks ? <span className="range-marks">{marks.map((mark) => <span key={mark}>{mark}</span>)}</span> : null}
    </label>
  );
}

export function Stepper({ label, value, onChange, min = 0, max, step = 1 }: NumberProps) {
  const set = (next: number) => onChange(Math.min(max ?? Number.MAX_SAFE_INTEGER, Math.max(min, next)));
  return (
    <span className="field-label">
      {label}
      <span className="stepper">
        <NumericInput label={label} value={value} onChange={set} min={min} max={max} step={step} mode="numeric" />
        <button type="button" aria-label={`Diminuir: ${label}`} onClick={() => set(value - step)}><Icon name="remove" size={18} /></button>
        <button type="button" aria-label={`Aumentar: ${label}`} onClick={() => set(value + step)}><Icon name="add" size={18} /></button>
      </span>
    </span>
  );
}

export function SelectField({ label, value, onChange, options }: { label: string; value: string; onChange: (value: string) => void; options: { value: string; label: string }[] }) {
  return (
    <label className="field-label">
      {label}
      <span className="field-shell">
        <select className="field" value={value} onChange={(event) => onChange(event.target.value)}>
          {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
        <Icon name="expand_more" size={20} className="select-caret" />
      </span>
    </label>
  );
}

export function Segmented<T extends string>({ label, value, onChange, options, variant }: { label: string; value: T; onChange: (value: T) => void; options: { value: T; label: string; icon?: string }[]; variant?: 'auto' | 'bar' | 'box' }) {
  return (
    <div className={`segmented${variant ? ` ${variant}` : ''}`} role="group" aria-label={label}>
      {options.map((option) => (
        <button key={option.value} type="button" aria-pressed={option.value === value} className={option.value === value ? 'active' : ''} onClick={() => onChange(option.value)}>
          {option.icon ? <Icon name={option.icon} size={16} /> : null}{option.label}
        </button>
      ))}
    </div>
  );
}

export function ToggleRow({ label, icon, value, onChange }: { label: string; icon: string; value: boolean; onChange: (value: boolean) => void }) {
  return (
    <div className="toggle-row">
      <span className="toggle-label"><Icon name={icon} size={20} />{label}</span>
      <button type="button" className="toggle" aria-pressed={value} aria-label={label} onClick={() => onChange(!value)}><i /></button>
    </div>
  );
}
